"use client";

import type { AnalyticsEvent } from "./analytics-events";

/**
 * Cliente-side analytics wrapper para Google Analytics 4 (gtag.js).
 *
 * Cada llamada a {@link track} se traduce a `gtag('event', name, params)`
 * que GA4 enruta a la propiedad configurada por `NEXT_PUBLIC_GA4_MEASUREMENT_ID`.
 *
 * Beneficios de centralizar aquí (vs llamar a `gtag` desde components):
 *   - Tipado fuerte sobre {@link AnalyticsEvent} — el catálogo es
 *     fuente única de verdad, evita typos en nombres de eventos.
 *   - SSR-safe: si window no existe (server component / build), no-op.
 *   - Si gtag aún no cargó (Provider está pendiente o adblocker lo
 *     mata), el llamado falla silencioso en vez de tirar la app.
 *
 * Properties siguen el schema GA4:
 *   - Para eventos de e-commerce estándar (purchase, add_to_cart, etc.)
 *     usar `currency` + `value` + `items[]` con la estructura recomendada.
 *     Ver https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 *   - Para eventos custom, mantener propiedades flat (1 nivel) — los
 *     reportes de GA4 se filtran mejor con primitivos.
 */
export function track(
  event: AnalyticsEvent,
  properties?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
  if (typeof gtag !== "function") return;
  gtag("event", event, properties ?? {});
}

/**
 * Asocia el `user.id` a la sesión analítica actual. Útil para juntar
 * eventos pre-login y post-login bajo el mismo `user_pseudo_id` en GA4.
 *
 * `user_id` en GA4 nunca debe ser PII directa (no email). Usar UUID
 * interno; el email puede ir como user property aparte si Marketing
 * lo necesita para Audiences.
 */
export function identify(userId: string): void {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
  if (typeof gtag !== "function") return;
  gtag("set", "user_properties", { user_id: userId });
  // `config` con user_id activa cross-device stitching cuando GA tiene
  // Signals on. Property set arriba sirve como fallback.
  const id = readMeasurementId();
  if (id) gtag("config", id, { user_id: userId });
}

/**
 * Limpia el user_id de la sesión analítica (logout). GA4 vuelve a
 * usar el cookie pseudo id para la próxima visita.
 */
export function resetIdentity(): void {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as { gtag?: GtagFn }).gtag;
  if (typeof gtag !== "function") return;
  gtag("set", "user_properties", { user_id: undefined });
}

function readMeasurementId(): string | undefined {
  return process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
}

type GtagFn = (...args: unknown[]) => void;
